import { useCallback, useEffect, useState } from "react";

import { toast } from "@upstageai/ui";
import _ from "lodash";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { keyBoardManage } from "../atom/function/parsingTablekeyBoardManger";
import ErrorDialog from "../components/Dialog/ErrorDialog";
import { getModelListApiUrl } from "../constants/ApiUrl";
import { EndpointType, getShowConfig } from "../constants/Endpoints";
import { MODEL_FETCH_FAIL } from "../constants/ErrorMessage";
import {
  DEMO_MODE_FEATURE,
  FEATURE,
  ONPREMISE_DEMO_FEATURE,
} from "../constants/FeatureFlag";
import useAuth from "../hooks/useAuth";
import { useDevice } from "../hooks/useDevice";
import {
  INFERENCE_STATE,
  InferenceState,
  Model,
  ResultFile,
  ValidatorFileProvider,
  ValidatorModelsProvider,
} from "../hooks/useFile";
import { useIframeListener } from "../hooks/useIframeListener";
import { useIframeSignal } from "../hooks/useIframeSignal";
import { ConfigPanel } from "../molecules/config/ConfigPanel";
import MobileFooter, { MobileViewConfig } from "../molecules/MobileFooter";
import { ResultView } from "../molecules/result-file/ResultView";
import DocumentControlPanel from "../organisms/DocumentControlPanel";
import { ValidatorDetailView } from "../organisms/ValidatorDetailView";
import ValidatorImageView from "../organisms/ValidatorImageView";
import { ImageOacJson } from "../types/demo";
import {
  ExtractorOacJsonResponse,
  ImageExtractorOacJson,
} from "../types/extractorOacResponse";
import { ImageLAOacJson, LAOacJsonResponse } from "../types/laOacResponse";
import { ImageOCROacJson, OCROacJsonResponse } from "../types/ocrOacResponse";
import { Ontology } from "../types/ontology";
import { getModel, getResultFile } from "../utils/file";
import { endpointStore } from "../utils/mobx/EndpointStore";
import { envVarStore } from "../utils/mobx/EnvVarStore";
import { imageViewStore } from "../utils/mobx/ImageViewStore";
import { parsingLAStore } from "../utils/mobx/ParsingLAStore";
import { parsingOCRStore } from "../utils/mobx/ParsingOCRStore";
import { parsingTableStore } from "../utils/mobx/ParsingTableStore";
import { resultInteractionStore } from "../utils/mobx/ResultInteractionStore";
import {
  processExtractorResult,
  processLAResult,
  processOCRResult,
} from "../utils/resultProcessor";
import { cn } from "../utils/style";
type ModelResponse = Model[];

const isDocumentDRSP = (
  endpointType: string,
  models: Model[],
  selectedModelIndex: number,
  result?: ExtractorOacJsonResponse,
): boolean => {
  if (endpointType !== "extractor") return false;
  const model = getModel(models, selectedModelIndex);
  if (!model) return false;

  const isDRSPModel = model.type?.toLowerCase() === "drsp";
  const isReceiptDocument =
    result?.documentType?.toLowerCase()?.includes("receipt") ?? false;

  return isDRSPModel || isReceiptDocument;
};

function Home({
  isLogin = false,
  newEndpointType = "" as EndpointType,
}: {
  isLogin: boolean;
  newEndpointType: EndpointType; // TODO : "" 제거
}) {
  // 데모화면에 파일 업로드 시 하나의 파일은 여러 이미지로 구성될 수 있음
  const { t } = useTranslation();
  const [inferenceState, setInferenceState] = useState<InferenceState>(
    INFERENCE_STATE.IDLE,
  );

  const [selectedFileIndex, setSelectedFileIndex] = useState<number>(-1);
  const [selectedFile, setSelectedFile] = useState<ResultFile | null>(null);
  const [page, setPage] = useState<number>(0);

  const { isMobile } = useDevice();
  const [viewConfig, setViewConfig] = useState<MobileViewConfig>({
    mode: "image",
    resultView: "preview",
  });

  const [image, setImage] = useState<string>("");
  const [imageScale, setImageScale] = useState<number>(1.0);
  const [numberOfImages, setNumberOfImages] = useState<number>(0);

  const [selectedModelIndex, setSelectedModelIndex] = useState<number>(0);

  const [files, setFiles] = useState<ResultFile[]>([]);
  const [models, setModels] = useState<Model[]>([]);

  const [errorOn, setErrorOn] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<number>(1);
  const [errorReloadFunction, setErrorReloadFunction] = useState<() => void>(
    () => {},
  );

  const [isIframeReady, setIsIframeReady] = useState<boolean>(false);

  const {
    setResult,
    setHoveredBoxIds,
    setSelectedBoxIds,
    setFocusIndex,
    orgResults,
    setOrgResults,
  } = resultInteractionStore;
  const { setResizeBlocker } = imageViewStore;
  const { setOntologies, changeExtractorFile } = parsingTableStore;

  const { endpointType, setEndpointType, setIsLogin } = endpointStore;

  const { changeOCRFile } = parsingOCRStore;
  const { changeLAFile } = parsingLAStore;
  const { loginRequired } = envVarStore;

  const [isWide, setIsWide] = useState<boolean>(true);
  const handleWindowResize = useCallback(() => {
    setIsWide(!window.matchMedia(`(max-width: 1200px)`).matches);
  }, [setIsWide]);

  useEffect(() => {
    setIsWide(!window.matchMedia(`(max-width: 1200px)`).matches);
  }, [setIsWide]);

  useEffect(() => {
    setResizeBlocker(true);
    setTimeout(() => {
      setResizeBlocker(false);
    }, 310);
  }, [setResizeBlocker]);

  useEffect(() => {
    const mediaQueryList = window.matchMedia(`(max-width: 1200px)`);

    // window resize 시 모바일 여부 상태 설정
    mediaQueryList.addEventListener("change", handleWindowResize);

    return () => {
      mediaQueryList.removeEventListener("change", handleWindowResize);
    };
  }, [handleWindowResize]);

  const [searchParams] = useSearchParams();

  const { auth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (FEATURE === ONPREMISE_DEMO_FEATURE) {
      let targetPath = "";

      if (newEndpointType === "extractor") {
        targetPath = "kie";
      } else if (newEndpointType === "ocr") {
        targetPath = "ocr";
      } else if (newEndpointType === "layout-analysis") {
        targetPath = "la";
      }

      if (loginRequired === "true" && !auth.user) {
        navigate(`/before-login/${targetPath}`);
      } else {
        navigate(`/${targetPath}`);
      }
    }
  }, [loginRequired]);

  // validator 시나리오에서만 home를 로드할 때 결과를 조회해서 가져온다.
  useEffect(() => {
    const requestId = searchParams.get("requestId");
    const token = searchParams.get("token");
    const userId = searchParams.get("userId");

    if (FEATURE === DEMO_MODE_FEATURE || FEATURE === ONPREMISE_DEMO_FEATURE) {
      return;
    }

    async function loadResult() {
      const { backendUrl } = envVarStore;
      try {
        setInferenceState(INFERENCE_STATE.NEW);
        let url = `${backendUrl}/api/result/${requestId}`;
        if (token !== null && token !== undefined) {
          url += `?token=${token}`;
          if (userId !== null) {
            url += `&userId=${userId}`;
          }
        } else if (userId !== null) {
          url += `?userId=${userId}`;
        }

        const response = await fetch(url, {
          method: "GET",
        });
        if (200 <= response.status && response.status < 300) {
          if (endpointType.toLowerCase() === "extractor") {
            const respOac =
              (await response.json()) as unknown as ExtractorOacJsonResponse;
            setOrgResults([...orgResults, _.cloneDeep(respOac)]);
            processExtractorResult(
              respOac,
              respOac.metadata.pages &&
                respOac.metadata.pages.length > 0 &&
                respOac.metadata.pages[0].height > 0,
            ).then(() => {
              let modelIndex = models.findIndex(
                model => model.name === respOac.modelVersion,
              );
              if (modelIndex === -1) {
                modelIndex = models.length;
                setModels([
                  ...models,
                  {
                    name: respOac.modelVersion,
                    type: "custom",
                    url: "",
                    serviceName: "document-ai",
                    status: "public",
                  },
                ]);
              }
              setSelectedModelIndex(modelIndex);
              const newFile = {
                id: uuidv4(),
                title: respOac.filename,
                result: respOac,
                modelIndex: modelIndex,
              };
              setFiles([newFile]);
              setSelectedFile(newFile);
              setSelectedFileIndex(0);
              setInferenceState(INFERENCE_STATE.IDLE);
              setOntologies(respOac.ontologies as unknown as Ontology[]);
            });
          } else if (endpointType.toLowerCase() === "ocr") {
            const respOac =
              (await response.json()) as unknown as OCROacJsonResponse;
            processOCRResult(
              respOac,
              respOac.metadata.pages && respOac.metadata.pages[0].height > 0,
            ).then(() => {
              let modelIndex = models.findIndex(
                model => model.name === respOac.modelVersion,
              );
              if (modelIndex === -1) {
                modelIndex = models.length;
                setModels([
                  ...models,
                  {
                    name: respOac.modelVersion,
                    type: "custom",
                    url: "",
                    serviceName: "document-ai",
                    status: "public",
                  },
                ]);
              }
              setSelectedModelIndex(modelIndex);
              const newFile = {
                id: uuidv4(),
                title: respOac.filename,
                result: respOac,
                modelIndex: modelIndex,
              };
              setFiles([newFile]);
              setSelectedFile(newFile);
              setSelectedFileIndex(0);
              setInferenceState(INFERENCE_STATE.IDLE);
            });
          } else if (endpointType.toLowerCase() === "layout-analysis") {
            const respOac =
              (await response.json()) as unknown as LAOacJsonResponse;
            processLAResult(
              respOac,
              respOac.metadata.pages && respOac.metadata.pages[0].height > 0,
            ).then(() => {
              let modelIndex = models.findIndex(
                model => model.name === respOac.model,
              );
              if (modelIndex === -1) {
                modelIndex = models.length;
                setModels([
                  ...models,
                  {
                    name: respOac.model,
                    type: "custom",
                    url: "",
                    serviceName: "document-ai",
                    status: "public",
                  },
                ]);
              }
              setSelectedModelIndex(modelIndex);
              const newFile = {
                id: uuidv4(),
                title: respOac.filename,
                result: respOac,
                modelIndex: modelIndex,
              };
              setFiles([newFile]);
              setSelectedFile(newFile);
              setSelectedFileIndex(0);
              setInferenceState(INFERENCE_STATE.IDLE);
            });
          }
        } else {
          console.error("fetch error:", response.status, response.statusText);
          setErrorType(response.status);
          setErrorOn(true);
          setErrorReloadFunction(() => () => {
            setErrorOn(false);
          });
          setInferenceState(INFERENCE_STATE.IDLE);
        }
      } catch (err) {
        console.error("err:", err);
        setErrorOn(true);
        setInferenceState(INFERENCE_STATE.IDLE);
      }
    }

    loadResult();
  }, [searchParams, setFiles]);

  useEffect(() => {
    async function settingModels() {
      await envVarStore.fetchEnvVars();
      const { backendUrl } = envVarStore;

      setEndpointType(newEndpointType);
      setIsLogin(isLogin);

      // demo 모드가 아닌 경우 모델 정보를 불러올 필요가 없다.
      if (FEATURE !== ONPREMISE_DEMO_FEATURE) return;

      setModels([]);

      try {
        const apiUrl = getModelListApiUrl(backendUrl, newEndpointType);
        const response = await fetch(apiUrl, {
          method: "GET",
        });

        if (response.status < 300 && response.status >= 200) {
          const modelsResponse = (await response.json()) as ModelResponse;
          const newModels: Model[] = [];
          modelsResponse.forEach(model => {
            newModels.push({
              name: model.name,
              type: model.type ?? "",
              url: model.url,
              serviceName: "document-ai",
              status: "public",
            });
          });
          newModels.sort((a, b) => a.name.localeCompare(b.name));
          setModels(newModels);
        }
      } catch (err) {
        console.error("err:", err);
        setErrorType(MODEL_FETCH_FAIL);
        setErrorOn(true);
        setErrorReloadFunction(() => () => {
          setErrorOn(false);
          window.location.reload();
        });
      }
    }
    settingModels();

    window.addEventListener("keydown", keyBoardManage);

    return () => {
      window.removeEventListener("keydown", keyBoardManage);
    };
  }, [newEndpointType]);

  if (FEATURE === DEMO_MODE_FEATURE) {
    // 데모 부모 창 신호 전송
    useIframeSignal({ isIframeReady });

    // 데모 부모 창 메시지 수신
    useIframeListener({
      setModels,
      setIsIframeReady,
    });
  }

  useEffect(() => {
    const file = getResultFile(files, selectedFileIndex);
    if (!file) return;

    setSelectedFile(file);
    setPage(0);
    setImage(file.result.imageBase64[0]);
    setNumberOfImages(file.result.imageBase64.length);

    if (file.result.imageBase64.length > 30) {
      setNumberOfImages(30);
      toast({
        title: t(`errors.max_page_title`),
        description: t(`errors.max_page_description`),
      });
    }
    setSelectedModelIndex(file.modelIndex);
    if (endpointType.toLowerCase() === "extractor") {
      setResult((file.result as ImageExtractorOacJson)?.fields);
      changeExtractorFile(
        (file.result as ImageExtractorOacJson)?.fields,
        file.id,
      );
    } else if (endpointType.toLowerCase() === "ocr") {
      setResult((file.result as ImageOCROacJson)?.pages);
      changeOCRFile((file.result as ImageOCROacJson)?.pages, file.id);
    } else if (endpointType.toLowerCase() === "layout-analysis") {
      setResult((file.result as ImageLAOacJson)?.elements);
      changeLAFile((file.result as ImageLAOacJson)?.elements, file.id);
    }
    setFocusIndex(-1);
  }, [
    files,
    selectedFileIndex,
    selectedModelIndex,
    setFocusIndex,
    setResult,
    changeExtractorFile,
  ]);

  // 페이지 변경시 보이는 이미지를 바꾼다.
  useEffect(() => {
    const file = getResultFile(files, selectedFileIndex);
    if (!file) return;

    setImage(file.result?.imageBase64[page] ?? "");
  }, [page]);

  const showConfig =
    getShowConfig(endpointType) && FEATURE === DEMO_MODE_FEATURE;
  const [isConfigSidebarOpen, setIsConfigSidebarOpen] = useState(!isMobile);

  useEffect(() => {
    setIsConfigSidebarOpen(!isMobile);
  }, [isMobile]);

  // const resetValidator = () => {
  //   setSelectedFileIndex(-1);
  //   setSelectedFile(null);
  //   setPage(0);
  //   setImage("");
  //   setImageScale(1.0);
  //   setNumberOfImages(0);
  //   setHoveredBoxIds([]);
  //   setSelectedBoxIds([]);
  //   setWorkToolVisible(false);
  //   setSelectedModelIndex(0);
  //   setModalOpen(false);
  //   setFiles([]);
  //   window.open("about:blank", "_self");
  //   window.close();
  // };

  return (
    <ValidatorFileProvider
      value={{
        files: files,
        selectedFileIndex,
        inferenceState,
        setSelectedFileIndex,
        setFiles: setFiles,
        setInferenceState,
      }}
    >
      <ValidatorModelsProvider
        value={{
          models: models,
          selectedModelIndex,
          setSelectedModelIndex,
        }}
      >
        {/* 문서 업로드 컨트롤 패널 */}
        <DocumentControlPanel />

        {/* 작업창 부분 */}
        <div className="relative flex h-full w-full overflow-hidden">
          <div
            className={cn(
              "relative flex w-full flex-row overflow-auto border-t bg-muted mobile:flex-col mobile:divide-y mobile:pb-[theme(height.mobile-footer)] desktop:divide-x",
              "transition-all duration-300",
              showConfig && isConfigSidebarOpen
                ? "z-10 desktop:w-[calc(100%-theme(width.config-sidebar))]"
                : "desktop:w-full",
            )}
          >
            {/*  이미지 부분 */}
            <section className="grid flex-1 gap-2 mobile:max-w-full desktop:min-w-[50%]">
              {viewConfig.mode === "image" && (
                <ValidatorImageView
                  isDRSP={isDocumentDRSP(
                    endpointType,
                    models,
                    selectedModelIndex,
                    selectedFile?.result as ExtractorOacJsonResponse,
                  )}
                  file={selectedFile?.result as ImageOacJson}
                  page={page}
                  pageSelectHandler={setPage}
                  image={image}
                  imageScale={imageScale}
                  imageScaleChangeHandler={setImageScale}
                  numberOfImages={numberOfImages}
                  inferenceState={inferenceState}
                />
              )}
              {viewConfig.mode === "result" && (
                <ResultView view={viewConfig.resultView} dragging={false} />
              )}
            </section>

            {/* 가운데 결과창 부분 */}
            <section
              className={cn({
                "grid flex-1 grid-cols-1 gap-3 mobile:max-w-full desktop:min-w-[50%]":
                  true,
                "transition-all duration-300": isWide,
                hidden: isMobile,
              })}
              onClick={() => {
                setSelectedBoxIds([]);
                setHoveredBoxIds([]);
                setFocusIndex(-1);
              }}
            >
              <div className="flex flex-row">
                <ValidatorDetailView
                  file={files[selectedFileIndex]?.result as ImageOacJson}
                />
              </div>
            </section>

            {/* 모바일 전용 푸터 */}
            {isMobile && (
              <MobileFooter
                viewConfig={viewConfig}
                setViewConfig={setViewConfig}
              />
            )}
          </div>

          {/* Config 패널 */}
          <ConfigPanel
            showConfig={showConfig}
            isConfigSidebarOpen={isConfigSidebarOpen}
            setIsConfigSidebarOpen={setIsConfigSidebarOpen}
            endpointType={endpointType}
          />
        </div>

        <ErrorDialog
          open={errorOn}
          setOpen={setErrorOn}
          errorType={errorType}
          errorCloseFunction={() => setErrorOn(false)}
          errorReloadFunction={errorReloadFunction}
        />
      </ValidatorModelsProvider>
    </ValidatorFileProvider>
  );
}
export default observer(Home);
